import { Heart, MapPin, ArrowUpRight, Download, Star, Globe, Clock, Sparkles } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1562774053-701939374585?w=800&q=80";
const src = (u) => (u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const Chip = ({ children, testId }) => (
  <span data-testid={testId} className="text-[10px] font-semibold bg-navy/5 text-navy px-2 py-0.5 rounded-full">{children}</span>
);

function CardMedia({ item, isFav, onToggle, disabled }) {
  const badge = item.ranking ? `#${item.ranking}` : item.level;
  return (
    <div className="relative h-40 overflow-hidden">
      <img src={src(item.image) || FALLBACK} alt={item.title} onError={(e) => { if (e.currentTarget.src !== FALLBACK) e.currentTarget.src = FALLBACK; }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
      <div className="absolute inset-0 bg-gradient-to-t from-navy/70 to-transparent" />
      {badge && (
        <span data-testid={`card-badge-${item.external_id}`} className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wide bg-orange text-white px-2.5 py-1 rounded-full">{badge}</span>
      )}
      {item.score && (
        <span data-testid={`card-score-${item.external_id}`} className="absolute bottom-3 left-3 inline-flex items-center gap-1 text-xs font-bold bg-white text-navy px-2.5 py-1 rounded-full">
          <Star className="h-3 w-3 text-orange" fill="currentColor" /> {item.score}
        </span>
      )}
      {item.logo && <img src={src(item.logo)} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="absolute bottom-3 right-3 h-9 w-9 rounded-lg bg-white object-contain p-1" />}
      <button
        data-testid={`fav-toggle-${item.external_id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggle(item); }}
        disabled={disabled}
        aria-label={isFav ? "Remove from favorites" : "Add to favorites"}
        className={`absolute top-3 right-3 h-9 w-9 rounded-full flex items-center justify-center transition-colors ${isFav ? "bg-orange text-white" : "bg-white/90 text-navy hover:bg-white"}`}
      >
        <Heart className="h-4 w-4" fill={isFav ? "currentColor" : "none"} />
      </button>
    </div>
  );
}

function CardBody({ item }) {
  const chips = [...(item.languages || []), ...(item.attendance || [])];
  return (
    <div className="p-4">
      <h3 className="font-display font-extrabold italic text-navy leading-tight">{item.title}</h3>
      <p className="text-sm text-muted-foreground">{item.subtitle}</p>
      {item.location && (
        <div className="flex items-start gap-1.5 mt-3 text-xs text-muted-foreground" data-testid={`card-location-${item.external_id}`}>
          <MapPin className="h-3.5 w-3.5 shrink-0 mt-px" /> {item.location}
        </div>
      )}
      {item.duration && (
        <div className="flex items-center gap-1.5 mt-1.5 text-xs font-semibold text-navy" data-testid={`card-duration-${item.external_id}`}>
          <Clock className="h-3.5 w-3.5" /> {item.duration}
        </div>
      )}
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 mt-2.5" data-testid={`card-chips-${item.external_id}`}>
          <Globe className="h-3.5 w-3.5 text-muted-foreground" />
          {chips.map((c) => <Chip key={c}>{c}</Chip>)}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-orange">{item.category}</span>
        {item.url && <span className="text-xs font-bold text-navy inline-flex items-center group-hover:text-orange">View <ArrowUpRight className="h-3.5 w-3.5 ml-0.5" /></span>}
      </div>
    </div>
  );
}

export function CatalogCard({ item, isFav, onToggle, onAskAI, disabled }) {
  const cls = "block bg-white rounded-2xl border border-border overflow-hidden shadow-card hover:shadow-pop transition-[box-shadow,transform] duration-300 hover:-translate-y-1";
  const inner = (<><CardMedia item={item} isFav={isFav} onToggle={onToggle} disabled={disabled} /><CardBody item={item} /></>);
  return (
    <div className="group" data-testid={`catalog-card-${item.external_id}`}>
      {item.url ? (
        <a href={item.url} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`card-link-${item.external_id}`}>{inner}</a>
      ) : (
        <div className={cls}>{inner}</div>
      )}
      {onAskAI && (
        <button onClick={() => onAskAI(item)} data-testid={`ask-ai-${item.external_id}`}
          className="mt-2 flex items-center justify-center gap-1.5 w-full rounded-full bg-orange hover:bg-orange-dark text-white text-xs font-bold py-2.5 transition-colors">
          <Sparkles className="h-3.5 w-3.5" /> Ask the AI about this {item.item_type === "program" ? "program" : "school"}
        </button>
      )}
      {item.brochure_url && (
        <a href={item.brochure_url} target="_blank" rel="noopener noreferrer" data-testid={`brochure-${item.external_id}`}
          className="mt-2 flex items-center justify-center gap-1.5 w-full rounded-full bg-navy/5 hover:bg-orange hover:text-white text-navy text-xs font-bold py-2.5 transition-colors">
          <Download className="h-3.5 w-3.5" /> Download brochure
        </a>
      )}
    </div>
  );
}
